import React, { useCallback, useState } from 'react'
import { Popover } from '@mui/material'
import IconButton from 'particles/Button/IconButton'
import Text from 'particles/Text'
import { useNewTranslation } from 'hooks'
import { CAMPAIGN_STATUS_ENUM, CampaignState } from 'state/campaigns'
import styled from 'particles/themes/styled'
import { theme } from 'theme'
import { TaskSections } from '..'
import SaveAndSubmitButtonGroup from '../../SaveAndSubmitButtonGroup'
import AddStepButton from './AddStepButton'
import EditCampaignRestricted from 'components/Shared/Permission/EditCampaignRestricted'
import RectangleButton from 'particles/Button/RectangleButton'
import { usePermission } from 'hooks/permissions'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'

interface CustomSaveAndSubmitButtonGroupProps {
    campaign?: CampaignState
    disableSubmit?: boolean
    disableSave?: boolean
    onSave: (e: React.MouseEvent<HTMLButtonElement>) => void
    onSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void
    onStepAdded: VoidFunction
    selectedTaskSection: TaskSections
}

const CustomSaveAndSubmitButtonGroup: React.FC<CustomSaveAndSubmitButtonGroupProps> = ({
    campaign,
    disableSave,
    disableSubmit,
    onSubmit,
    onSave,
    onStepAdded,
    selectedTaskSection,
}) => {
    const [t] = useNewTranslation()
    const campaignStatus = campaign?.status
    const isAbleToReview =
        usePermission(CampaignStatusControl.ReviewCampaign) && campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_REVIEW
    const isAbleToLaunch =
        (campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            campaignStatus === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            campaignStatus === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)
    const [moreButtonEl, setMoreButtonEl] = useState<HTMLButtonElement | null>(null)
    const buttonText = [CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT].includes(campaignStatus!)
        ? t('button.sendToReview')
        : t('button.launchCampaign')
    const handleMoreButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        setMoreButtonEl(e.currentTarget)
    }, [])
    const handleCloseMorePopup = useCallback(() => setMoreButtonEl(null), [])

    // we wll only show add step button when campaign status is draft or for edit and task section is STEPS
    return (campaignStatus !== CAMPAIGN_STATUS_ENUM.DRAFT && campaignStatus !== CAMPAIGN_STATUS_ENUM.FOR_EDIT) ||
        selectedTaskSection !== TaskSections.STEPS ? (
        <EditCampaignRestricted
            campaignStatus={campaignStatus}
            isEditDeadline={selectedTaskSection === TaskSections.SCHEDULE}
            isAbleToReview={isAbleToReview}
            isAbleToLaunch={isAbleToLaunch}
        >
            <SaveAndSubmitButtonGroup
                campaign={campaign}
                disableSubmit={disableSubmit}
                disableSave={disableSave}
                onSubmit={onSubmit}
                onSave={onSave}
            />
        </EditCampaignRestricted>
    ) : (
        <EditCampaignRestricted campaignStatus={campaignStatus}>
            <div className="button-group">
                <div className="button-group--left" style={{ order: 1 }}>
                    <AddStepButton onStepAdded={onStepAdded} campaignStatus={campaignStatus} />
                </div>
                {[CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT, CAMPAIGN_STATUS_ENUM.FOR_REVIEW].includes(
                    campaignStatus,
                ) && (
                    <>
                        <EditCampaignRestricted campaignStatus={campaignStatus} isEditBrief isEditDeadline>
                            <RectanglePublishButton
                                form="outlined"
                                size="m"
                                label={buttonText}
                                isDisabled={disableSubmit}
                                onClick={onSubmit}
                            />
                        </EditCampaignRestricted>
                        <MoreButton
                            form="icon"
                            size="m"
                            color="primary"
                            icon="dotsThreeOutline"
                            iconForm="fill"
                            type="button"
                            isDisabled={disableSubmit}
                            onClick={handleMoreButtonClick}
                        />
                        <Popover
                            open={Boolean(moreButtonEl)}
                            anchorEl={moreButtonEl}
                            onClose={handleCloseMorePopup}
                            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            PaperProps={{
                                style: {
                                    boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.12)',
                                    marginTop: '-' + theme.newSpacing.xs,
                                },
                            }}
                        >
                            <MoreMenuContainer>
                                <button
                                    type="button"
                                    onClick={disableSubmit ? undefined : onSubmit}
                                    disabled={disableSubmit}
                                >
                                    <Text scale="bodyS400">{buttonText}</Text>
                                </button>
                            </MoreMenuContainer>
                        </Popover>
                    </>
                )}
                <RectangleSaveButton
                    form="filled"
                    size="m"
                    label={t('button.saveChanges')}
                    isDisabled={disableSave}
                    onClick={onSave}
                />
            </div>
        </EditCampaignRestricted>
    )
}

const RectanglePublishButton = styled(RectangleButton)`
    display: none;
    @media screen and (min-width: 540px) {
        display: flex;
        order: 2;
    }
`
const MoreButton = styled(IconButton)`
    order: 3;
    display: flex;
    @media screen and (min-width: 540px) {
        display: none;
        order: 2;
    }
`
const MoreMenuContainer = styled.div`
    & > button {
        padding: ${theme.newSpacing.m} ${theme.newSpacing.s};
        cursor: pointer;
        width: 100%;
        border: 0;
        border-radius: 4px;
        background: ${theme.newColors.neutral50};
        text-align: left;
        transition: background 150ms ease-in-out;
        color: ${theme.newColors.neutral500};

        &:not(:disabled):hover {
            background: ${theme.newColors.primary100};
            color: ${theme.newColors.neutral600};
        }
        &:disabled {
            color: ${theme.newColors.neutral300};
            cursor: default;
        }
    }
`
const RectangleSaveButton = styled(RectangleButton)`
    order: 2;
    @media screen and (min-width: 540px) {
        order: 3;
    }
`

export default CustomSaveAndSubmitButtonGroup
